import { useState, type FormEvent, type ReactNode } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  actionLabel,
  CHANNEL_STATUS_LABELS,
  CHANNEL_STATUSES,
  CREATIVE_SLOTS,
  isOwnedChannel,
  segmentLabel,
  triggerLabel,
  type AutomationRule,
  type CampaignAudience,
  type CampaignChannel,
  type CampaignCreative,
  type CampaignDetail,
  type CampaignPatch,
  type ChannelPatch,
  type CreativePatch,
} from '@/api/campaigns'
import { Badge, Card, formatCurrency, type FormatOptions } from '@/design'

import { KindBadge } from '../components'
import { amountValue, AudienceFields, AutomationFields, Field, parseAmount } from '../fields'
import { useCampaignMutation } from '../hooks'
import { useEditableSection, useRunner } from '../runner'

function useCampaignUpdate(campaignId: string) {
  const client = useApiClient()
  return useCampaignMutation((patch: CampaignPatch) => client.updateCampaign(campaignId, patch))
}

function EditToggle({ editing, onToggle, label, canManage }: { editing: boolean; onToggle: () => void; label: string; canManage: boolean }) {
  if (!canManage) return null
  return (
    <button type="button" className="ci-button" data-variant="ghost" aria-expanded={editing} onClick={onToggle}>
      {editing ? 'Cancel' : `Edit ${label}`}
    </button>
  )
}

function Chips({ items, empty }: { items: readonly string[]; empty: ReactNode }) {
  if (items.length === 0) return <span className="camp-muted">{empty}</span>
  return (
    <ul className="camp-chips">
      {items.map((item) => (
        <li key={item}>
          <Badge>{item}</Badge>
        </li>
      ))}
    </ul>
  )
}

// --- channels and budget --------------------------------------------------------------

function ChannelRow({ campaignId, channel, canManage, money }: { campaignId: string; channel: CampaignChannel; canManage: boolean; money: FormatOptions }) {
  const client = useApiClient()
  const mutation = useCampaignMutation((patch: ChannelPatch) => client.updateCampaignChannel(campaignId, channel.id, patch))
  const { run, message } = useRunner()
  const [budget, setBudget] = useState(amountValue(channel.budget))
  const owned = isOwnedChannel(channel.channelType)

  return (
    <div className="camp-panel">
      <div className="camp-panel__header">
        <span className="camp-inline">
          <h3 className="camp-panel__title">{channel.name}</h3>
          <KindBadge kind={channel.kind} />
        </span>
        {canManage ? (
          <select
            aria-label={`${channel.name} status`}
            value={channel.status}
            disabled={mutation.isPending}
            onChange={(event) => void run(() => mutation.mutateAsync([{ status: event.target.value as CampaignChannel['status'] }]), 'The status could not be changed.')}
          >
            {CHANNEL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {CHANNEL_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        ) : (
          <Badge>{CHANNEL_STATUS_LABELS[channel.status]}</Badge>
        )}
      </div>
      {owned ? (
        <p className="camp-muted">Owned channel — no media budget.</p>
      ) : canManage ? (
        <form
          className="camp-form"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => mutation.mutateAsync([{ budget: parseAmount(budget) }]), 'The budget could not be saved.')
          }}
        >
          <Field label={`${channel.name} budget`} hint={channel.spend !== null ? `Spent so far: ${formatCurrency(channel.spend, money)}` : undefined}>
            {(props) => <input {...props} type="number" min="0" step="0.01" inputMode="decimal" value={budget} onChange={(event) => setBudget(event.target.value)} />}
          </Field>
          <div className="camp-form__actions">
            <button type="submit" className="ci-button" disabled={mutation.isPending || budget === amountValue(channel.budget)}>
              Save {channel.name} budget
            </button>
          </div>
        </form>
      ) : (
        <p>Budget: {channel.budget === null ? 'None set' : formatCurrency(channel.budget, money)}</p>
      )}
      {message}
    </div>
  )
}

export function ChannelsBudgetCard({ detail, canManage, money }: { detail: CampaignDetail; canManage: boolean; money: FormatOptions }) {
  const { campaign, channels } = detail
  const update = useCampaignUpdate(campaign.id)
  const { run, message } = useRunner()
  const [total, setTotal] = useState(amountValue(campaign.totalBudget))
  const planned = channels.reduce((sum, channel) => sum + (channel.budget ?? 0), 0)
  const over = campaign.totalBudget !== null && planned > campaign.totalBudget

  return (
    <Card title="Channels and budget" subtitle={`${channels.length} channels · ${formatCurrency(planned, money)} planned across channels`}>
      <div className="camp-panels">
        {canManage ? (
          <form
            className="camp-form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              void run(() => update.mutateAsync([{ totalBudget: parseAmount(total) }]), 'The budget could not be saved.')
            }}
          >
            <Field label="Campaign total budget" error={over ? 'Channel budgets add up to more than this total.' : null} hint="Blank for no overall cap.">
              {(props) => <input {...props} type="number" min="0" step="0.01" inputMode="decimal" value={total} onChange={(event) => setTotal(event.target.value)} />}
            </Field>
            <div className="camp-form__actions">
              <button type="submit" className="ci-button" disabled={update.isPending || total === amountValue(campaign.totalBudget)}>
                Save total budget
              </button>
            </div>
          </form>
        ) : (
          <p>Total budget: {campaign.totalBudget === null ? 'None set' : formatCurrency(campaign.totalBudget, money)}</p>
        )}
        {message}
        {channels.map((channel) => (
          <ChannelRow key={channel.id} campaignId={campaign.id} channel={channel} canManage={canManage} money={money} />
        ))}
      </div>
    </Card>
  )
}

// --- creative -------------------------------------------------------------------------

function CreativePanel({ campaignId, creative, channel, canManage }: { campaignId: string; creative: CampaignCreative; channel: CampaignChannel | undefined; canManage: boolean }) {
  const client = useApiClient()
  const mutation = useCampaignMutation((patch: CreativePatch) => client.updateCampaignCreative(campaignId, creative, patch))
  const section = useEditableSection({ headline: creative.headline ?? '', body: creative.body ?? '', assetUrl: creative.assetUrl ?? '' })
  const { editing, draft: form, setDraft: setForm } = section
  const slot = CREATIVE_SLOTS[channel?.channelType ?? 'custom']

  return (
    <section className="camp-panel" aria-label={creative.name}>
      <div className="camp-panel__header">
        <span className="camp-inline">
          <h3 className="camp-panel__title">{creative.name}</h3>
          {channel ? <KindBadge kind={channel.kind} /> : null}
        </span>
        <EditToggle editing={editing} onToggle={section.toggle} label={creative.name} canManage={canManage} />
      </div>
      {editing ? (
        <form
          className="camp-form"
          onSubmit={(event) => {
            event.preventDefault()
            void section.save(
              () => mutation.mutateAsync([{ headline: form.headline || null, body: form.body || null, assetUrl: form.assetUrl || null }]),
              'The creative could not be saved.',
            )
          }}
        >
          {slot.fields.headline ? (
            <Field label={slot.fields.headline}>
              {(props) => <input {...props} value={form.headline} onChange={(event) => setForm({ ...form, headline: event.target.value })} />}
            </Field>
          ) : null}
          {slot.fields.assetUrl ? (
            <Field label={slot.fields.assetUrl}>
              {(props) => <input {...props} type="url" value={form.assetUrl} onChange={(event) => setForm({ ...form, assetUrl: event.target.value })} />}
            </Field>
          ) : null}
          {slot.fields.body ? (
            <Field label={slot.fields.body} wide>
              {(props) => <textarea {...props} rows={3} value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} />}
            </Field>
          ) : null}
          <div className="camp-form__actions camp-form__wide">
            <button type="submit" className="ci-button" data-variant="primary" disabled={mutation.isPending}>
              Save creative
            </button>
          </div>
        </form>
      ) : (
        <dl className="camp-summary">
          <dt>Channel</dt>
          <dd>
            {channel?.name ?? '—'} · {slot.label}
          </dd>
          {creative.headline ? (
            <>
              <dt>{slot.fields.headline ?? 'Headline'}</dt>
              <dd>{creative.headline}</dd>
            </>
          ) : null}
          {creative.body ? (
            <>
              <dt>{slot.fields.body ?? 'Copy'}</dt>
              <dd>{creative.body}</dd>
            </>
          ) : null}
          {creative.assetUrl ? (
            <>
              <dt>Asset</dt>
              <dd>
                <a href={creative.assetUrl} target="_blank" rel="noreferrer noopener">
                  {creative.assetUrl}
                </a>
              </dd>
            </>
          ) : null}
        </dl>
      )}
      {section.message}
    </section>
  )
}

export function CreativeCard({ detail, canManage }: { detail: CampaignDetail; canManage: boolean }) {
  const without = detail.channels.filter((channel) => !detail.creatives.some((creative) => creative.channelId === channel.id))
  return (
    <Card title="Creative" subtitle="The asset each channel carries.">
      <div className="camp-panels">
        {detail.creatives.map((creative) => (
          <CreativePanel
            key={creative.id}
            campaignId={detail.campaign.id}
            creative={creative}
            channel={detail.channels.find((channel) => channel.id === creative.channelId)}
            canManage={canManage}
          />
        ))}
        {without.length > 0 ? <p className="camp-muted">No creative yet for {without.map((channel) => channel.name).join(', ')}.</p> : null}
      </div>
    </Card>
  )
}

// --- audience -------------------------------------------------------------------------

export function AudienceCard({ detail, canManage }: { detail: CampaignDetail; canManage: boolean }) {
  const { campaign } = detail
  const update = useCampaignUpdate(campaign.id)
  const { editing, toggle, draft: audience, setDraft: setAudience, save, message } = useEditableSection<CampaignAudience>(campaign.audience)
  const conflict = audience.include.some((segment) => audience.exclude.includes(segment))

  return (
    <Card
      title="Audience"
      actions={
        <EditToggle
          editing={editing}
          onToggle={toggle}
          label="audience"
          canManage={canManage}
        />
      }
    >
      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (conflict) return
            void save(() => update.mutateAsync([{ audience }]), 'The audience could not be saved.')
          }}
        >
          <AudienceFields
            audience={audience}
            onChange={(patch) => setAudience((current) => ({ ...current, ...patch }))}
            error={conflict ? 'A segment cannot be both included and excluded.' : null}
          />
          <div className="camp-form__actions">
            <button type="submit" className="ci-button" data-variant="primary" disabled={update.isPending || conflict}>
              Save audience
            </button>
          </div>
        </form>
      ) : (
        <dl className="camp-summary">
          <dt>Include</dt>
          <dd>
            <Chips items={campaign.audience.include.map(segmentLabel)} empty="Everyone" />
          </dd>
          <dt>Exclude</dt>
          <dd>
            <Chips items={campaign.audience.exclude.map(segmentLabel)} empty="Nobody" />
          </dd>
          <dt>Geography</dt>
          <dd>
            <Chips items={campaign.audience.geography} empty="Anywhere" />
          </dd>
        </dl>
      )}
      {message}
    </Card>
  )
}

// --- automation -----------------------------------------------------------------------

export function AutomationCard({ detail, canManage }: { detail: CampaignDetail; canManage: boolean }) {
  const { campaign } = detail
  const update = useCampaignUpdate(campaign.id)
  const { editing, toggle, draft: rules, setDraft: setRules, save, message } = useEditableSection<AutomationRule[]>(campaign.automations)

  return (
    <Card
      title="Automation"
      actions={
        <EditToggle
          editing={editing}
          onToggle={toggle}
          label="automation"
          canManage={canManage}
        />
      }
    >
      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void save(() => update.mutateAsync([{ automations: rules }]), 'The automation could not be saved.')
          }}
        >
          <AutomationFields rules={rules} onChange={setRules} />
          <div className="camp-form__actions">
            <button type="submit" className="ci-button" data-variant="primary" disabled={update.isPending}>
              Save automation
            </button>
          </div>
        </form>
      ) : campaign.automations.length === 0 ? (
        <p className="camp-muted">No automation triggers.</p>
      ) : (
        <ul className="camp-activity">
          {campaign.automations.map((rule) => (
            <li key={rule.trigger}>
              <span>
                {triggerLabel(rule.trigger)} → {actionLabel(rule.action)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {message}
    </Card>
  )
}

// --- activity -------------------------------------------------------------------------

const WHEN = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export function ActivityCard({ detail }: { detail: CampaignDetail }) {
  return (
    <Card title="Activity">
      {detail.activity.length === 0 ? (
        <p className="camp-muted">Nothing yet.</p>
      ) : (
        <ol className="camp-activity" aria-label="Campaign activity">
          {detail.activity.map((entry) => (
            <li key={entry.id}>
              <span>{entry.message}</span>
              <time dateTime={entry.at}>{WHEN.format(new Date(entry.at))}</time>
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}
